"use client";

import { MobileIcon } from "@radix-ui/react-icons";
import { CloudIcon, ComputerIcon, StarIcon, WandIcon } from "lucide-react";
import Image from "next/image";

type IconComponent = typeof StarIcon;

const TAB_ICONS: Record<string, IconComponent> = {
	maps: StarIcon,
	practice: CloudIcon,
};

const HIGHLIGHT_ICONS = [WandIcon, ComputerIcon, MobileIcon];

export interface MarketingFeatureHighlight {
	title: string;
	description: string;
}

export interface MarketingFeatureTab {
	id: string;
	title: string;
	subtitle: string;
	description: string;
	highlights: MarketingFeatureHighlight[];
}

export interface MarketingFeaturesProps {
	kicker: string;
	title: string;
	lead: string;
	tabs: MarketingFeatureTab[];
	imageSrc: string;
}

export function MarketingFeatures(props: MarketingFeaturesProps) {
	return (
		<section id="features" className="scroll-my-20 py-12 lg:py-16 xl:py-24">
			<div className="container">
				<div className="mb-6 max-w-3xl lg:mb-0">
					<small className="mb-4 block font-medium text-primary text-xs uppercase tracking-wider">
						{props.kicker}
					</small>
					<h2 className="font-medium text-3xl lg:text-4xl xl:text-5xl">
						{props.title}
					</h2>
					<p className="mt-2 text-base text-foreground/60 lg:text-lg">
						{props.lead}
					</p>
				</div>
			</div>

			<div>
				<div className="container mt-8 grid grid-cols-1 gap-8 md:gap-12 lg:mt-12 lg:gap-16 xl:gap-24">
					{props.tabs.map((tab) => {
						const Icon = TAB_ICONS[tab.id] ?? StarIcon;
						const highlights = tab.highlights ?? [];

						return (
							<div key={tab.id}>
								<div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2">
									<div>
										<Image
											src={props.imageSrc}
											alt={tab.title}
											className="h-auto w-full max-w-xl"
											width={1000}
											height={800}
										/>
									</div>

									<div>
										<h3 className="font-normal text-lg text-foreground leading-tight md:text-xl lg:text-2xl">
											<span className="inline-flex items-center gap-2 font-medium">
												<Icon className="size-5 shrink-0 text-primary" />
												{tab.title}.{" "}
											</span>
											<span className="font-sans">
												{tab.subtitle}
											</span>
										</h3>

										<p className="mt-4 text-foreground/60">
											{tab.description}
										</p>
									</div>
								</div>

								{highlights.length > 0 ? (
									<div className="mt-8 grid gap-6 sm:grid-cols-2 lg:mt-12 lg:grid-cols-3">
										{highlights.map((highlight, k) => {
											const HighlightIcon =
												HIGHLIGHT_ICONS[
													k % HIGHLIGHT_ICONS.length
												];
											return (
												<div
													key={`${tab.id}-highlight-${k}`}
													className="flex flex-col items-stretch justify-between rounded-2xl bg-card p-4 lg:p-6"
												>
													<div>
														<HighlightIcon
															className="text-primary text-xl"
															width="1em"
															height="1em"
														/>
														<strong className="mt-2 block font-medium text-lg">
															{highlight.title}
														</strong>
														<p className="mt-1 text-sm">
															{
																highlight.description
															}
														</p>
													</div>
												</div>
											);
										})}
									</div>
								) : null}
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}
